import {
    Background,
    BackgroundVariant,
    MiniMap,
    NodeChange,
    ReactFlow,
    useReactFlow,
    type Edge,
    type Node,
    type OnMove,
} from "@xyflow/react";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { descendants, LayoutEdge, LayoutNode, OrgTree, TreeItem } from "../layout";
import { ROOT_ID } from "../types";
import { BusEdgeData, edgeTypes } from "./BusEdge";
import { useTreeState } from "./context";
import { DropMode, NodeData } from "./context";
import { nodeTypes } from "./nodes";
import { useAnimatedLayout } from "./useAnimatedLayout";

/**
 * Холст дерева: React Flow даёт масштаб, панораму, мини-карту и перетаскивание,
 * раскладку и анимацию считаем сами (см. layout.ts и useAnimatedLayout).
 *
 * Перетаскивание (только в режиме правки):
 *  - подразделение на середину другого — вложить внутрь, на верхнюю/нижнюю четверть —
 *    поставить соседом до/после; на свою же ветку — нельзя (красная рамка);
 *  - сотрудника на подразделение — перевод (с диалогом подтверждения).
 */

export type DropTarget = { id: string; mode: DropMode };

export interface DropRequest {
    dragged: TreeItem;
    target: TreeItem;
    mode: Exclude<DropMode, "invalid">;
}

interface OrgCanvasProps {
    tree: OrgTree;
    nodes: LayoutNode[];
    edges: LayoutEdge[];
    gapX: number;
    edit: boolean;
    onDrop(request: DropRequest): void;
    onDragState(dragging: string | null, drop: DropTarget | null): void;
    onZoom(zoom: number): void;
    /** масштаб или положение сменил пользователь (колесо, перетаскивание), а не код */
    onUserMove(): void;
    onPaneClick(): void;
}

const EDGE_ZONE = 0.28;

export const OrgCanvas = ({
    tree,
    nodes,
    edges,
    gapX,
    edit,
    onDrop,
    onDragState,
    onZoom,
    onUserMove,
    onPaneClick,
}: OrgCanvasProps) => {
    const frame = useAnimatedLayout(nodes);
    const flow = useReactFlow();
    const [dragPos, setDragPos] = useState<{ id: string; x: number; y: number } | null>(null);
    const dropRef = useRef<DropTarget | null>(null);
    const byId = useMemo(() => new Map(nodes.map((n) => [n.id, n])), [nodes]);

    const flowNodes = useMemo<Node<NodeData>[]>(
        () =>
            frame.map((f) => {
                const own = dragPos?.id === f.layout.id;
                return {
                    id: f.leaving ? `leaving:${f.layout.id}` : f.layout.id,
                    type: f.layout.kind,
                    position: own ? { x: dragPos!.x, y: dragPos!.y } : { x: f.x, y: f.y },
                    width: f.layout.w,
                    height: f.layout.h,
                    data: { layout: f.layout, delay: f.delay, leaving: f.leaving },
                    draggable: edit && !f.leaving && (f.layout.kind === "unit" || f.layout.kind === "employee"),
                    selectable: false,
                    focusable: false,
                    zIndex: own ? 1000 : f.layout.kind === "add" ? 0 : 1,
                    // React Flow гасит события у узлов, которые нельзя тащить и выделять (режим просмотра),
                    // а карточки кликабельны всегда: карточка узла, ФИО, раскрытие ветки
                    style: { pointerEvents: f.leaving ? "none" : "all" },
                };
            }),
        [frame, dragPos, edit]
    );

    const { path } = useTreeState();
    const flowEdges = useMemo<Edge<BusEdgeData>[]>(
        () =>
            edges.map((e) => ({
                id: e.id,
                source: e.source,
                target: e.target,
                type: "bus",
                data: { level: e.level, bus: Math.round(gapX * 0.42) },
                // связи детей одного родителя идут по общему стволу и лежат друг на друге -
                // подсвеченный путь поднимаем над остальными, иначе соседняя линия его перекрывает
                zIndex: path.has(e.source) && path.has(e.target) ? 1 : 0,
                focusable: false,
                selectable: false,
            })),
        [edges, gapX, path]
    );

    const onNodesChange = useCallback((changes: NodeChange<Node<NodeData>>[]) => {
        changes.forEach((c) => {
            if (c.type === "position" && c.position && c.dragging) {
                setDragPos({ id: c.id, x: c.position.x, y: c.position.y });
            }
        });
    }, []);

    /** Узел под указателем и что с ним сделать */
    const resolveDrop = useCallback(
        (draggedId: string, clientX: number, clientY: number): DropTarget | null => {
            const dragged = tree.byId.get(draggedId);
            if (!dragged) {
                return null;
            }
            const point = flow.screenToFlowPosition({ x: clientX, y: clientY });
            const hit = nodes.find(
                (n) =>
                    n.id !== draggedId &&
                    n.kind !== "add" &&
                    point.x >= n.x &&
                    point.x <= n.x + n.w &&
                    point.y >= n.y &&
                    point.y <= n.y + n.h
            );
            if (!hit) {
                return null;
            }

            // на сотрудника — значит в его подразделение
            const targetNode =
                (hit.kind === "employee" || hit.kind === "vacancy") && hit.parentId
                    ? (byId.get(hit.parentId) ?? hit)
                    : hit;
            const target = targetNode.item;

            if (dragged.kind === "employee") {
                if (target.kind !== "unit") {
                    return { id: targetNode.id, mode: "invalid" };
                }
                return dragged.parentId === target.id ? null : { id: targetNode.id, mode: "inside" };
            }

            if (descendants(dragged).has(target.id)) {
                return { id: targetNode.id, mode: "invalid" };
            }
            if (target.kind === "org") {
                return { id: ROOT_ID, mode: "inside" };
            }

            const rel = (point.y - targetNode.y) / targetNode.h;
            if (hit === targetNode && rel < EDGE_ZONE) {
                return { id: targetNode.id, mode: "before" };
            }
            if (hit === targetNode && rel > 1 - EDGE_ZONE) {
                return { id: targetNode.id, mode: "after" };
            }
            return dragged.parentId === target.id ? null : { id: targetNode.id, mode: "inside" };
        },
        [tree, nodes, byId, flow]
    );

    const onNodeDragStart = useCallback(
        (_: MouseEvent | TouchEvent, node: Node<NodeData>) => {
            dropRef.current = null;
            onDragState(node.id, null);
        },
        [onDragState]
    );

    const onNodeDrag = useCallback(
        (event: MouseEvent | TouchEvent, node: Node<NodeData>) => {
            const point = "touches" in event ? (event.touches[0] ?? event.changedTouches[0]) : event;
            if (!point) {
                return;
            }
            const drop = resolveDrop(node.id, point.clientX, point.clientY);
            const prev = dropRef.current;
            if (prev?.id !== drop?.id || prev?.mode !== drop?.mode) {
                dropRef.current = drop;
                onDragState(node.id, drop);
            }
        },
        [resolveDrop, onDragState]
    );

    const onNodeDragStop = useCallback(
        (_: MouseEvent | TouchEvent, node: Node<NodeData>) => {
            const drop = dropRef.current;
            dropRef.current = null;
            setDragPos(null);
            onDragState(null, null);

            const dragged = tree.byId.get(node.id);
            const target = drop ? tree.byId.get(drop.id) : undefined;
            if (dragged && target && drop && drop.mode !== "invalid") {
                onDrop({ dragged, target, mode: drop.mode });
            }
        },
        [tree, onDrop, onDragState]
    );

    const handleMove: OnMove = useCallback((_, viewport) => onZoom(viewport.zoom), [onZoom]);

    return (
        <ReactFlow
            nodes={flowNodes}
            edges={flowEdges}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            onNodesChange={onNodesChange}
            onNodeDragStart={onNodeDragStart}
            onNodeDrag={onNodeDrag}
            onNodeDragStop={onNodeDragStop}
            onMove={handleMove}
            onMoveStart={(event) => event && onUserMove()}
            onPaneClick={onPaneClick}
            nodesConnectable={false}
            elementsSelectable={false}
            nodesDraggable={edit}
            nodeDragThreshold={6}
            panOnScroll
            zoomOnScroll={false}
            zoomOnPinch
            zoomOnDoubleClick={false}
            minZoom={0.2}
            maxZoom={1.6}
            onlyRenderVisibleElements
            attributionPosition="bottom-left"
            autoPanOnNodeDrag
        >
            <Background variant={BackgroundVariant.Dots} gap={22} size={1.6} />
            <MiniMap
                pannable
                zoomable
                position="bottom-right"
                nodeColor={(n) =>
                    n.type === "org" ? "#00273d" : n.type === "unit" ? "rgba(0,39,61,.45)" : "rgba(0,39,61,.15)"
                }
                maskColor="rgba(247,249,251,.7)"
                nodeBorderRadius={3}
                style={{ width: 150, height: 100 }}
            />
        </ReactFlow>
    );
};
